<template>
	<div class="cbs-datetime-control">
		<legend class="flex justify-between items-center">
			<div>{{ localValue.title }}<span class="text-danger" v-if="!localValue.is_optional">&nbsp;*</span>:</div>
			<cbs-hint
				v-if="localValue.description && localValue.description.enable && localValue.description.position === $constant.DYNAMIC_FORM_FIELD_INFO_POSITION.ICON"
				:data="localValue.description && localValue.description.value"
			/>
		</legend>

		<div class="my-1" v-if="localValue.description && localValue.description.enable && localValue.description.position === $constant.DYNAMIC_FORM_FIELD_INFO_POSITION.TOP">
			{{ localValue.description && localValue.description.value }}
		</div>
		<vx-input-group
			v-if="
				localTypeAttributeValue === $constant.DYNAMIC_FORM_FIELD_CONTROL_TYPES.DATETIME.TYPE_DATE ||
					localTypeAttributeValue === $constant.DYNAMIC_FORM_FIELD_CONTROL_TYPES.DATETIME.TYPE_DATETIME
			"
		>
			<template slot="append">
				<div class="append-text text-black">
					<i class="nucleo nucleo-calendar-date" @click="triggerClickDatetimePicker(localValue.id)"></i>
				</div>
			</template>

			<cbs-date-picker
				clearable
				class="w-full"
				prefix-icon="''"
				v-validate="!localValue.is_optional ? 'required' : ''"
				v-model="localValueTrigger"
				:type="datePickerType.type"
				:data-vv-as="localValue.title"
				format="DD-MM-yyyy HH:mm"
				:picker-options="dateTimePickerOptions"
				:ref="`datetime-picker-${localValue.id}`"
				:value-format="datePickerType.valueFormat"
				:name="`${localValue.title}-${localValue.id}`"
				:placeholder="`${$t('Actions.Choose')} ${localValue.title.toLowerCase()}`"
			/>
		</vx-input-group>
		<el-time-select
			v-else-if="localTypeAttributeValue === $constant.DYNAMIC_FORM_FIELD_CONTROL_TYPES.DATETIME.TYPE_TIME"
			class="flex w-full"
			format="HH:mm"
			value-format="HH:mm"
			v-validate="!localValue.is_optional ? 'required' : ''"
			v-model="localValueTrigger"
			:data-vv-as="localValue.title"
			:picker-options="timePickerOptions"
			:name="`${localValue.title}-${localValue.id}`"
			:placeholder="`${$t('Actions.Choose')} ${localValue.title.toLowerCase()}`"
		/>
		<span class="text-danger text-sm">{{ errors.first(`${localValue.title}-${localValue.id}`) }}</span>
		<div class="my-1" v-if="localValue.description && localValue.description.enable && localValue.description.position === $constant.DYNAMIC_FORM_FIELD_INFO_POSITION.BOTTOM">
			{{ localValue.description && localValue.description.value }}
		</div>
	</div>
</template>

<script>
import moment from "moment";

import { TimeSelect } from "element-ui";
import CbsHint from "@/components/cbs-hint/CbsHint.vue";

export default {
	name: "DatetimeControl",
	inject: ["parentValidator"],
	components: {
		CbsHint,
		"el-time-select": TimeSelect
	},
	props: {
		value: {
			type: Object,
			required: true,
			validator: prop =>
				prop.hasOwnProperty("id") &&
				prop.hasOwnProperty("value") &&
				prop.hasOwnProperty("title") &&
				prop.hasOwnProperty("control") &&
				prop.hasOwnProperty("children") &&
				prop.hasOwnProperty("attributes") &&
				prop.hasOwnProperty("is_optional") &&
				prop.hasOwnProperty("description")
		}
	},
	computed: {
		localValue: {
			get() {
				const parsedValueWithMoment = this.value.value && moment(this.value.value).isValid() ? moment(this.value.value).format("YYYY-MM-DD HH:mm:ss") : this.value.value;
				return { ...this.value, value: parsedValueWithMoment };
			},
			set(val) {
				this.$emit("input", val);
			}
		},
		localValueTrigger: {
			get() {
				return this.localValue.value;
			},
			set(val) {
				let nextValue = val;
				if (
					this.localTypeAttributeValue === this.$constant.DYNAMIC_FORM_FIELD_CONTROL_TYPES.DATETIME.TYPE_DATE ||
					this.localTypeAttributeValue === this.$constant.DYNAMIC_FORM_FIELD_CONTROL_TYPES.DATETIME.TYPE_DATETIME
				) {
					if (this.checkIsInvalidDateByValidationRules(nextValue)) {
						// if invalid date => set value to old value
						nextValue = this.localValue.value;
					}
				}
				if (nextValue === null) {
					nextValue = "";
				}
				this.$emit("input", { ...this.localValue, value: nextValue });
			}
		},
		localAttributesValue() {
			const attributesValue = typeof this.localValue.attributes === "string" ? JSON.parse(this.localValue.attributes) : this.localValue.attributes;
			return attributesValue;
		},
		localTypeAttributeValue() {
			return this.localAttributesValue.type || this.$constant.DYNAMIC_FORM_FIELD_CONTROL_TYPES.DATETIME.TYPE_DATETIME;
		},
		localValidationAttributeValue() {
			return this.localAttributesValue.validation;
		},
		localModeValidationAttributeValue() {
			return this.localValidationAttributeValue.mode || this.$constant.DYNAMIC_FORM_FIELD_CONTROL_TYPES.DATETIME_VALIDATION.TYPE_DATE_RANGE;
		},
		localFromNowValidationAttributeValue() {
			return this.localValidationAttributeValue.from_now || {};
		},
		datePickerType() {
			if (this.localTypeAttributeValue === this.$constant.DYNAMIC_FORM_FIELD_CONTROL_TYPES.DATETIME.TYPE_DATE) {
				return {
					type: "date",
					valueFormat: "yyyy-MM-dd",
					format: this.userDateFormat
						.split("D")
						.join("d")
						.split("Y")
						.join("y")
				};
			}
			if (this.localTypeAttributeValue === this.$constant.DYNAMIC_FORM_FIELD_CONTROL_TYPES.DATETIME.TYPE_DATETIME) {
				return {
					type: "datetime",
					valueFormat: "yyyy-MM-dd HH:mm:ss",
					format: this.userDateTimeFormat
						.split("D")
						.join("d")
						.split("Y")
						.join("y")
				};
			}
			return {};
		},
		timePickerOptions() {
			const options = {};
			if (this.localModeValidationAttributeValue === this.$constant.DYNAMIC_FORM_FIELD_CONTROL_TYPES.DATETIME_VALIDATION.TYPE_AFTER_CURRENT_TIME) {
				options.end = "23:59";
				options.step = "00:15";
				options.start = "00:00";

				if (this.localFromNowValidationAttributeValue.value) {
					const addedStartDate = moment()
						.local()
						.add(Number(this.localFromNowValidationAttributeValue.value), "hours");
					// if date after added hours is greater than end of current date set null to options start
					const isAddedStartDateInvalid = moment(addedStartDate)
						.add(15, "minutes")
						.isAfter(moment().local(), "day");
					options.start = isAddedStartDateInvalid ? null : addedStartDate.format("HH:mm");
				}
			}
			return options;
		},
		validationDatePickerRules() {
			let beforeDateCompare, afterDateCompare;
			const currentDate = moment().local();
			if (
				this.localModeValidationAttributeValue === this.$constant.DYNAMIC_FORM_FIELD_CONTROL_TYPES.DATETIME_VALIDATION.TYPE_AFTER_CURRENT_TIME &&
				this.localFromNowValidationAttributeValue.value
			) {
				beforeDateCompare = currentDate.add(Number(this.localFromNowValidationAttributeValue.value), "hours");
			}

			if (this.localModeValidationAttributeValue === this.$constant.DYNAMIC_FORM_FIELD_CONTROL_TYPES.DATETIME_VALIDATION.TYPE_DATE_RANGE) {
				if (this.localValidationAttributeValue && this.localValidationAttributeValue.min) {
					beforeDateCompare = moment(this.localValidationAttributeValue.min, "YYYY-MM-DD");
				}
				if (this.localValidationAttributeValue && this.localValidationAttributeValue.max) {
					afterDateCompare = moment(this.localValidationAttributeValue.max, "YYYY-MM-DD");
				}
			}

			return { beforeDateCompare, afterDateCompare };
		},
		dateTimePickerOptions() {
			const options = {};
			const { beforeDateCompare, afterDateCompare } = this.validationDatePickerRules;

			if ((beforeDateCompare && afterDateCompare) || (beforeDateCompare && !afterDateCompare) || (afterDateCompare && !beforeDateCompare)) {
				options.disabledDate = date => this.checkIsInvalidDateByValidationRules(date);
			}
			return options;
		}
	},
	methods: {
		triggerClickDatetimePicker(fieldId) {
			this.$refs[`datetime-picker-${fieldId}`].$children[0].pickerVisible = true;
		},
		handleChangeValue(value) {
			this.localValueTrigger = value;
		},
		checkIsInvalidDateByValidationRules(dateToCompare) {
			if (!moment(dateToCompare).isValid()) {
				return false;
			}

			let isInvalidValue;
			const { beforeDateCompare, afterDateCompare } = this.validationDatePickerRules;

			if (beforeDateCompare && afterDateCompare) {
				isInvalidValue =
					moment(dateToCompare)
						.local()
						.isBefore(beforeDateCompare, "day") ||
					moment(dateToCompare)
						.local()
						.isAfter(afterDateCompare, "day");
			}
			if (beforeDateCompare && !afterDateCompare) {
				isInvalidValue = moment(dateToCompare)
					.local()
					.isBefore(beforeDateCompare, "day");
			}
			if (afterDateCompare && !beforeDateCompare) {
				isInvalidValue = moment(dateToCompare)
					.local()
					.isBefore(afterDateCompare, "day");
			}
			return !!isInvalidValue;
		}
	},
	created() {
		if (this.parentValidator) {
			this.$validator = this.parentValidator;
		}
	},
	watch: {
		localValueTrigger() {
			if (this.localModeValidationAttributeValue && this.localModeValidationAttributeValue.value && this.$constant.DYNAMIC_FORM_FIELD_CONTROL_TYPES.DATETIME_VALIDATION.TYPE_AFTER_CURRENT_TIME) {
				if (this.localModeValidationAttributeValue === this.$constant.DYNAMIC_FORM_FIELD_CONTROL_TYPES.DATETIME_VALIDATION.TYPE_AFTER_CURRENT_TIME) {
					let duration = moment.duration(moment(new Date(this.localValueTrigger)).diff(moment(new Date())));
					if (duration.asHours() && Number(duration.asHours()) < Number(this.localFromNowValidationAttributeValue.value)) {
						// let opts = {
						// 	message: "Lôĩ rồi",
						// 	text: "Phải chọn mốc thời gian lớn hơn hiện tại là " + this.localFromNowValidationAttributeValue.value + " giờ"
						// };
						// this.$helper.notifyWarning({ context: this, options: opts });
						let elms = document.querySelectorAll(".vs-noti-bottom-right");
						for (let k in elms) {
							if (elms[k] && typeof elms[k] === "object") {
								elms[k].remove();
							}
						}
						this.$vs.notify({
							time: 50000,
							title: "Thông báo",
							text: "Phải chọn mốc thời gian lớn hơn hiện tại là " + this.localFromNowValidationAttributeValue.value + " giờ",
							iconPack: "feather",
							icon: "icon-alert-circle",
							color: "warning"
						});
						this.localValueTrigger = moment().add(Number(this.localFromNowValidationAttributeValue.value), "hours");
					}
				}
			}
		}
	}
};
</script>

<style lang="scss" scoped>
.cbs-datetime-control {
	::v-deep .el-input__icon {
		line-height: 50px;
	}
}
</style>
